import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { visaMfaAPI } from '../../services/api';

/* V70.149 — Hızlı Araçlar: Vize Bilgisi (MFA).
 * Kaynak: T.C. Dışişleri Bakanlığı "Türk Vatandaşlarının Tabi Olduğu Vize Uygulamaları".
 * Veri YEREL API'den gelir (arka planda günlük senkron) — bu ekran MFA'ya istek atmaz.
 * Ülke seç → 4 pasaport türü için normalize sonuç + resmi kaynak metni. */

const STATUS_META = {
  VISA_FREE: { text: 'Vizesiz', color: '#16a34a', bg: 'rgba(22,163,74,0.12)' },
  VISA_REQUIRED: { text: 'Vize gerekli', color: '#dc2626', bg: 'rgba(220,38,38,0.12)' },
  E_VISA: { text: 'E-vize', color: '#2563eb', bg: 'rgba(37,99,235,0.12)' },
  VISA_ON_ARRIVAL: { text: 'Varışta vize', color: '#d97706', bg: 'rgba(217,119,6,0.14)' },
  CONDITIONAL: { text: 'Koşullu giriş', color: '#7c3aed', bg: 'rgba(124,58,237,0.12)' },
  UNKNOWN: { text: 'Bilgi kontrol edilmeli', color: '#64748b', bg: 'rgba(100,116,139,0.14)' },
};

const PASSPORT_TABS = [
  { key: 'ORDINARY', label: 'Umuma Mahsus', dot: '#7f1d3a' },
  { key: 'SPECIAL', label: 'Hususi', dot: '#15803d' },
  { key: 'SERVICE', label: 'Hizmet', dot: '#6b7280' },
  { key: 'DIPLOMATIC', label: 'Diplomatik', dot: '#111827' },
];

const flagOf = (code) => (/^[A-Z]{2}$/.test(code || '')
  ? String.fromCodePoint(...[...code].map(ch => 0x1f1a5 + ch.charCodeAt(0)))
  : '🏳️');

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return iso.slice(0, 10); }
};

const durationText = (r) => {
  if (r.maxStayDays && r.periodDays) return `${r.periodDays} günde ${r.maxStayDays} gün kalış`;
  if (r.maxStayDays) return `${r.maxStayDays} gün kalış`;
  if (r.maxStayMonths) return `${r.maxStayMonths} ay kalış`;
  return null;
};

const VisaTool = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [countries, setCountries] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [empty, setEmpty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [query, setQuery] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [selected, setSelected] = useState(null); // ülke detay payload'ı
  const [detailLoading, setDetailLoading] = useState(false);
  const [passport, setPassport] = useState('ORDINARY');

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const wrapRef = useRef(null);

  const loadCountries = () => {
    setLoading(true);
    visaMfaAPI.getCountries()
      .then(res => {
        setCountries(res.data.countries || []);
        setLastSync(res.data.lastSync || null);
        setEmpty(!!res.data.empty);
        setErr(null);
      })
      .catch(e => setErr(e.response?.data?.error || 'Ülke listesi alınamadı'))
      .finally(() => setLoading(false));
  };

  useEffect(loadCountries, []);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setListOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pick = (c) => {
    setListOpen(false);
    setQuery(c.name);
    setDetailLoading(true);
    setSelected(null);
    setErr(null); // V71 — önceki hata bandı yeni sonucun üstünde kalmasın
    visaMfaAPI.getCountry(c.code)
      .then(res => setSelected(res.data))
      .catch(e => setErr(e.response?.data?.error || 'Ülke bilgisi alınamadı'))
      .finally(() => setDetailLoading(false));
  };

  const runSync = async () => {
    if (syncing) return;
    setSyncing(true);
    setSyncMsg(null);
    try {
      const res = await visaMfaAPI.sync();
      setSyncMsg(`✓ ${res.data.countriesFound} ülke güncellendi (${res.data.rulesChanged} kural değişti)`);
      loadCountries();
      if (selected) visaMfaAPI.getCountry(selected.countryCode).then(r => setSelected(r.data)).catch(() => {});
    } catch (e) {
      setSyncMsg('✗ ' + (e.response?.data?.error || e.message || 'Senkronizasyon başarısız'));
    } finally {
      setSyncing(false);
    }
  };

  const q = query.trim().toLocaleLowerCase('tr');
  const filtered = q
    ? countries.filter(c => c.name.toLocaleLowerCase('tr').includes(q) || c.code.toLocaleLowerCase('tr').includes(q))
    : countries;

  const rule = selected?.rules?.[passport] || null;
  const meta = rule ? (STATUS_META[rule.status] || STATUS_META.UNKNOWN) : null;
  const needsCare = rule && (rule.status === 'CONDITIONAL' || rule.status === 'UNKNOWN' || rule.reviewRequired);

  if (loading) return <div className="ftv-loading">Vize verisi yükleniyor…</div>;

  return (
    <div className="ftv">
      {err && <div className="ftv-err">{err}</div>}

      {empty ? (
        <div className="ftv-empty">
          <div className="ftv-empty-ic">🛂</div>
          <div>Vize verisi henüz yüklenmedi. Sistem MFA sayfasını arka planda günde bir kez çeker.</div>
          {isAdmin
            ? <button className="btn btn-primary" style={{ marginTop: 10 }} onClick={runSync} disabled={syncing}>{syncing ? 'Çekiliyor…' : 'Şimdi güncelle'}</button>
            : <div className="ftv-muted" style={{ marginTop: 6 }}>İlk yükleme için yöneticinize başvurun.</div>}
          {syncMsg && <div className="ftv-syncmsg">{syncMsg}</div>}
        </div>
      ) : (
        <>
          {/* Ülke arama */}
          <div className="ftv-search" ref={wrapRef}>
            <input
              className="ftv-input"
              placeholder="Ülke ara… (ör. Almanya, BAE, Japonya)"
              value={query}
              autoFocus
              onFocus={() => setListOpen(true)}
              onChange={e => { setQuery(e.target.value); setListOpen(true); }}
              onKeyDown={e => {
                if (e.key === 'Enter' && filtered.length > 0) pick(filtered[0]);
                if (e.key === 'Escape' && listOpen) { e.stopPropagation(); setListOpen(false); }
              }}
            />
            {listOpen && (
              <div className="ftv-list">
                {filtered.slice(0, 200).map(c => (
                  <button key={c.code} className="ftv-item" onClick={() => pick(c)}>
                    <span className="ftv-flag">{flagOf(c.code)}</span>
                    <span className="ftv-name">{c.name}</span>
                    <span className="ftv-dot" style={{ background: (STATUS_META[c.ordinaryStatus] || STATUS_META.UNKNOWN).color }} title={(STATUS_META[c.ordinaryStatus] || STATUS_META.UNKNOWN).text} />
                  </button>
                ))}
                {filtered.length === 0 && <div className="ftv-none">Sonuç yok</div>}
              </div>
            )}
          </div>

          {/* Pasaport türü */}
          <div className="ftv-passports">
            {PASSPORT_TABS.map(p => (
              <button
                key={p.key}
                className={`ftv-ptab${passport === p.key ? ' on' : ''}`}
                onClick={() => setPassport(p.key)}
              >
                <span className="ftv-pdot" style={{ background: p.dot }} />{p.label}
              </button>
            ))}
          </div>

          {/* Sonuç */}
          {detailLoading && <div className="ftv-loading">Yükleniyor…</div>}
          {!detailLoading && !selected && (
            <div className="ftv-hint">Ülke seçince sonuç burada görünür. Nokta renkleri umuma mahsus pasaport durumunu gösterir.</div>
          )}
          {!detailLoading && selected && !rule && (
            <div className="ftv-hint">Bu pasaport türü için {selected.countryName} kaydı yok. Başka bir pasaport türü seçin veya resmi kaynağı kontrol edin.</div>
          )}
          {!detailLoading && selected && rule && (
            <div className="ftv-result">
              <div className="ftv-result-head">
                <span className="ftv-result-flag">{flagOf(selected.countryCode)}</span>
                <div className="ftv-result-title">
                  <strong>{selected.countryName}</strong>
                  <span className="ftv-muted">{selected.sourceCountryName}</span>
                </div>
                <span className="ftv-status" style={{ color: meta.color, background: meta.bg }}>{meta.text}</span>
              </div>

              <div className="ftv-facts">
                {durationText(rule) && <div className="ftv-fact">🕒 {durationText(rule)}</div>}
                {rule.airportTransitVisaRequired && <div className="ftv-fact ftv-warn">✈️ Havalimanı transit vizesi gerekli</div>}
                {rule.identityCardEntry && <div className="ftv-fact">🪪 Kimlik kartıyla giriş mümkün (koşullara bakın)</div>}
                {needsCare && <div className="ftv-fact ftv-warn">⚠️ Kesin hüküm verilemiyor — resmi açıklamayı mutlaka okuyun, gerekirse ülke temsilciliğine danışın</div>}
              </div>

              {rule.conditions.length > 0 && (
                <ul className="ftv-conds">
                  {rule.conditions.slice(0, 6).map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              )}

              <details className="ftv-raw" open={needsCare}>
                <summary>Resmi kaynak açıklaması</summary>
                <div className="ftv-rawtext">{selected.rawText}</div>
                {selected.links.length > 0 && (
                  <div className="ftv-links">
                    {selected.links.slice(0, 4).map((l, i) => (
                      <a key={i} href={l} target="_blank" rel="noopener noreferrer">{l}</a>
                    ))}
                  </div>
                )}
              </details>
            </div>
          )}
        </>
      )}

      {/* Kaynak + senkron alt bilgisi */}
      <div className="ftv-foot">
        <span>
          Kaynak: T.C. Dışişleri Bakanlığı · Son kontrol: {fmtDate(selected?.sourceCheckedAt || lastSync?.finishedAt)}
        </span>
        {isAdmin && !empty && (
          <button className="ftv-syncbtn" onClick={runSync} disabled={syncing} title="MFA sayfasını şimdi çek">
            {syncing ? '⏳' : '🔄'} Güncelle
          </button>
        )}
      </div>
      {syncMsg && !empty && <div className="ftv-syncmsg">{syncMsg}</div>}
      <div className="ftv-muted ftv-disclaimer">
        Vize kuralları değişebilir; kesin bilgi için ilgili ülke temsilciliği esastır.
      </div>
    </div>
  );
};

export default VisaTool;
